import type { ReactNode } from 'react';

interface SelectableOptionCardProps {
  icon: ReactNode;
  title: string;
  description?: string;
  badge?: string;
  selected: boolean;
  onSelect: () => void;
  /** Smaller padding/icon box for screens with many options in view at once. Default false = current look. */
  compact?: boolean;
}

export function SelectableOptionCard({
  icon,
  title,
  description,
  badge,
  selected,
  onSelect,
  compact = false
}: Readonly<SelectableOptionCardProps>) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={`flex w-full items-center text-left transition-all ${
        compact
          ? 'gap-3 rounded-xl border-2 p-2.5'
          : 'gap-4 rounded-2xl border-2 p-4'
      } ${
        // Same selected/unselected border+fill as the card/eWallet brand steps
        // (SelectCardBrandStep.tsx / SelectEWalletBrandStep.tsx), not this card's own colours.
        selected
          ? 'border-[#5CB891] bg-[#f0faf5]'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <span
        className={`flex flex-shrink-0 items-center justify-center rounded-lg border border-[#CCCCCC] bg-white text-black ${
          compact ? 'h-10 w-10 [&_svg]:h-5 [&_svg]:w-5' : 'h-12 w-12'
        }`}
      >
        {icon}
      </span>
      <span className="flex flex-1 flex-col">
        <span className="flex flex-wrap items-center gap-2">
          <span
            className={`font-semibold text-[#0F172A] ${compact ? 'text-sm' : 'text-base'}`}
          >
            {title}
          </span>
          {badge && (
            <span className="rounded-full bg-[#5CB891] px-2 py-0.5 text-xs font-medium text-white">
              {badge}
            </span>
          )}
        </span>
        {description && (
          <span className="mt-1 text-sm text-[#0F172A]">{description}</span>
        )}
      </span>
      {/*
        Radio-dot markup copied exactly from SelectCardBrandStep.tsx / SelectEWalletBrandStep.tsx
        (an outlined circle with a filled dot when selected), replacing this card's own filled
        check-circle. Same fixed h-6 w-6 size as the brand steps use — no compact variant there,
        so none here either.
      */}
      <div
        className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 ${
          selected ? 'border-[#5CB891]' : 'border-gray-300'
        }`}
      >
        {selected && <div className="h-3 w-3 rounded-full bg-[#5CB891]" />}
      </div>
    </button>
  );
}
